/**
 * Measure tool: a ruler button in the toolbar. One measurement at a time, the
 * same steps every time (Nucleus4D style, so nobody gets lost): turning the
 * ruler on starts one, two taps on the scene finish it (two points, a line,
 * the straight-line distance), and then the big + at the bottom starts the
 * next. While points are being placed the + is a × that cancels. Between
 * measurements taps go back to the walk controls (click-to-move works).
 * Measurements stay until deleted, also after the tool is turned off.
 *
 * Deleting, two ways: tap a measurement's label to select it, then either the
 * × on the label or the bin next to the + (or Delete / Backspace). Tapping the
 * scene with one selected just deselects it. Esc cancels a measurement being
 * placed first, then turns the tool off.
 *
 * Points are shared: a click on an existing point (or near it: the cursor
 * snaps onto it) starts or ends a line THERE, and the two lines stay joined.
 * Pressing a point and dragging it moves it, and every line joined to it
 * follows. Points only take the pointer while the ruler is on, so looking
 * around never grabs one by accident.
 *
 * Help: the first time the ruler is turned on (per browser) a step-by-step
 * tour sits above the dock. Each step waits for the visitor to DO it (place
 * the start, the end, drag a point, select the number, delete it, press +),
 * with the thing it is about pulsing; the "?" on the hint pill restarts it.
 *
 * Preview (mouse only, a finger has no hover): a crosshair cursor, a dot where
 * a click would land, and a magnifier under the cursor showing the scene there
 * at LOUPE_ZOOM×, so a corner or an edge can be hit exactly. Nothing is fitted
 * to the surface: splats are too noisy for a plane under the cursor to hold
 * still. Once the first point is down a dashed line runs to the cursor with
 * the live distance, and snaps to vertical (green) or level (blue) when it is
 * within AXIS_SNAP_DEG of either; Shift places freely. Dragged points snap the
 * same way against their other end. No surface under the cursor = a
 * not-allowed cursor.
 *
 * Where a point lands: with collision voxels the voxel hit says WHICH surface
 * (voxels cannot see through a plain white wall the way splat centres can),
 * then the splat centres within a few cm of it along the ray say where that
 * surface's visible skin is, so the dot sits on what the visitor sees, not on
 * the face of a 5 cm voxel in front of it.
 *
 * Navigation keeps working while measuring: drags still look/orbit, only a
 * still, quick tap places a point (same 10 px test the walk controller uses).
 * Walk click-to-move is switched off while points are being placed, through
 * onActiveChange, otherwise one tap would both glide and measure.
 *
 * World units are read as metres, as everywhere else in the viewer (the walk
 * eye height of 1.6 is the same assumption).
 *
 * Drawing is an HTML/SVG overlay like the markers overlay: crisp at any zoom
 * and no extra render pass.
 */

import * as pc from 'playcanvas';

import { pickSplat } from '../scene/splat-pick';
import { resolveLang, type UiLang } from './lang';

export const ICON_RULER = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1.5 11.5l10-10 3 3-10 10z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M4.5 8.5l1.5 1.5M6.5 6.5l1 1M8.5 4.5l1.5 1.5M10.5 2.5l1 1" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';

const ICON_PLUS = '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 4v14M4 11h14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
const ICON_BIN = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 5h12M7 5V3.5h4V5M5 5l.8 9.5h6.4L13 5M7.5 8v4M10.5 8v4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** The first-measurement tour: one instruction per step, each step waiting
 *  for the visitor to do it, then a closing card. */
interface HelpText {
    title: string;
    steps: string[];
    doneTitle: string;
    doneText: string;
    skip: string;
    next: string;
    ok: string;
    of: string;      // "Step {n} of {m}"
}

/** Tour steps, by index: 0 start point, 1 end point, 2 drag a point,
 *  3 select the number, 4 delete it, 5 press +; TOUR_DONE is the closing card. */
const TOUR_DONE = 6;

const TEXT: Record<UiLang, {
    button: string; click: string; tap: string; idle: string; add: string; cancel: string; second: string; miss: string; del: string; tip: string; help: string;
    mouse: HelpText; touch: HelpText;
}> = {
    en: {
        tip: 'You can also measure distances',
        help: 'How it works',
        mouse: {
            title: 'Your first measurement',
            steps: [
                'Click where your measurement starts. The magnifier under the cursor helps you hit the exact spot.',
                'Now click where it ends. The line turns green when it is vertical and blue when it is level. Hold Shift to place it freely.',
                'Drag either end point to fine-tune it.',
                'To delete a measurement, first click its number.',
                'Now delete it: click the bin at the bottom, or the × on the number.',
                'Press New measurement to take the next one.'
            ],
            doneTitle: 'You are all set',
            doneText: 'Click a point to start a line from it. Click the ruler or press Esc when you are done; your measurements stay.',
            skip: 'Skip',
            next: 'Next',
            ok: 'Got it',
            of: 'Step {n} of {m}'
        },
        touch: {
            title: 'Your first measurement',
            steps: [
                'Tap where your measurement starts.',
                'Now tap where it ends. The line turns green when it is vertical and blue when it is level.',
                'Drag either end point to fine-tune it.',
                'To delete a measurement, first tap its number.',
                'Now delete it: tap the bin at the bottom, or the × on the number.',
                'Tap New measurement to take the next one.'
            ],
            doneTitle: 'You are all set',
            doneText: 'Tap a point to start a line from it. Tap the ruler when you are done; your measurements stay.',
            skip: 'Skip',
            next: 'Next',
            ok: 'Got it',
            of: 'Step {n} of {m}'
        },
        button: 'Measure',
        click: 'Click where the measurement starts',
        tap: 'Tap where the measurement starts',
        idle: 'Press New measurement for another one',
        add: 'New measurement',
        cancel: 'Cancel',
        second: 'Now the second point',
        miss: 'No surface there. Try another spot',
        del: 'Delete measurement'
    },
    es: {
        tip: 'También puedes medir distancias',
        help: 'Cómo funciona',
        mouse: {
            title: 'Tu primera medida',
            steps: [
                'Haz clic donde empieza la medida. La lupa bajo el cursor te ayuda a acertar el punto exacto.',
                'Ahora haz clic donde termina. La línea se vuelve verde cuando es vertical y azul cuando está a nivel. Mantén Mayús para colocarla libremente.',
                'Arrastra cualquiera de los extremos para ajustarlo.',
                'Para borrar una medida, primero haz clic en su número.',
                'Ahora bórrala: haz clic en la papelera de abajo, o en la × del número.',
                'Pulsa Nueva medida para tomar la siguiente.'
            ],
            doneTitle: 'Ya está',
            doneText: 'Haz clic en un punto para empezar una línea desde él. Haz clic en la regla o pulsa Esc al terminar; tus medidas se quedan.',
            skip: 'Saltar',
            next: 'Siguiente',
            ok: 'Entendido',
            of: 'Paso {n} de {m}'
        },
        touch: {
            title: 'Tu primera medida',
            steps: [
                'Toca donde empieza la medida.',
                'Ahora toca donde termina. La línea se vuelve verde cuando es vertical y azul cuando está a nivel.',
                'Arrastra cualquiera de los extremos para ajustarlo.',
                'Para borrar una medida, primero toca su número.',
                'Ahora bórrala: toca la papelera de abajo, o la × del número.',
                'Toca Nueva medida para tomar la siguiente.'
            ],
            doneTitle: 'Ya está',
            doneText: 'Toca un punto para empezar una línea desde él. Toca la regla al terminar; tus medidas se quedan.',
            skip: 'Saltar',
            next: 'Siguiente',
            ok: 'Entendido',
            of: 'Paso {n} de {m}'
        },
        button: 'Medir',
        click: 'Haz clic donde empieza la medida',
        tap: 'Toca donde empieza la medida',
        idle: 'Pulsa Nueva medida para otra',
        add: 'Nueva medida',
        cancel: 'Cancelar',
        second: 'Ahora el segundo punto',
        miss: 'Ahí no hay superficie. Prueba en otro punto',
        del: 'Borrar medida'
    },
    ca: {
        tip: 'També pots mesurar distàncies',
        help: 'Com funciona',
        mouse: {
            title: 'La teva primera mesura',
            steps: [
                'Fes clic on comença la mesura. La lupa sota el cursor t’ajuda a encertar el punt exacte.',
                'Ara fes clic on acaba. La línia es torna verda quan és vertical i blava quan està a nivell. Mantén Maj per col·locar-la lliurement.',
                'Arrossega qualsevol dels extrems per ajustar-lo.',
                'Per esborrar una mesura, primer fes clic al seu número.',
                'Ara esborra-la: fes clic a la paperera de baix, o a la × del número.',
                'Prem Nova mesura per prendre la següent.'
            ],
            doneTitle: 'Ja està',
            doneText: 'Fes clic en un punt per començar una línia des d’allà. Fes clic al regle o prem Esc quan acabis; les mesures es queden.',
            skip: 'Salta',
            next: 'Següent',
            ok: 'Entesos',
            of: 'Pas {n} de {m}'
        },
        touch: {
            title: 'La teva primera mesura',
            steps: [
                'Toca on comença la mesura.',
                'Ara toca on acaba. La línia es torna verda quan és vertical i blava quan està a nivell.',
                'Arrossega qualsevol dels extrems per ajustar-lo.',
                'Per esborrar una mesura, primer toca el seu número.',
                'Ara esborra-la: toca la paperera de baix, o la × del número.',
                'Toca Nova mesura per prendre la següent.'
            ],
            doneTitle: 'Ja està',
            doneText: 'Toca un punt per començar una línia des d’allà. Toca el regle quan acabis; les mesures es queden.',
            skip: 'Salta',
            next: 'Següent',
            ok: 'Entesos',
            of: 'Pas {n} de {m}'
        },
        button: 'Mesurar',
        click: 'Fes clic on comença la mesura',
        tap: 'Toca on comença la mesura',
        idle: 'Prem Nova mesura per a una altra',
        add: 'Nova mesura',
        cancel: 'Cancel·la',
        second: 'Ara el segon punt',
        miss: 'Aquí no hi ha superfície. Prova en un altre punt',
        del: 'Esborra la mesura'
    }
};

/** The "you can measure" tip under the ruler: shown this long after load,
 *  for this long, until the ruler is first used (remembered per browser). */
const TIP_DELAY_MS = 2500;
const TIP_SHOW_MS = 9000;
const TIP_SEEN_KEY = 'ollo.measureTipSeen';
/** The tour: shown on the first switch-on, remembered per browser. A new key
 *  for the step-by-step tour of 4/11/2026, so everyone sees it once more. */
const HELP_SEEN_KEY = 'ollo.measureTourSeen';
/** Splat centres this far in front of / behind the voxel hit (m along the
 *  ray) count as that surface's skin. The voxel face sits up to a voxel (5 cm)
 *  in front of the centres it was built from. */
const SKIN_FRONT_M = 0.08;
const SKIN_BEHIND_M = 0.15;

const TAP_MAX_PX = 10;
const TAP_MAX_MS = 500;
/** Hover picks are a pass over every centre, so at most one per this many ms. */
const HOVER_PICK_MS = 70;
/** The cursor snaps onto an existing point within this many screen px. */
const MAGNET_PX = 14;
const SVG_NS = 'http://www.w3.org/2000/svg';
/** A line within this of vertical or level snaps to it (unless Shift is held). */
const AXIS_SNAP_DEG = 8;
/** The magnifier: diameter in css px, zoom, and the gap from the cursor to its top. */
const LOUPE_PX = 96;
const LOUPE_ZOOM = 3;
const LOUPE_GAP = 28;
/** Touch: hold a finger this long while placing and the magnifier comes up
 *  over it; slide to aim, lift to place (Ilias, 04/11/2026). A quicker tap
 *  still places at once, and a quick swipe still looks around. */
const AIM_HOLD_MS = 220;
/** Touch: the magnifier sits this far ABOVE the finger, which hides what is
 *  under it. */
const LOUPE_TOUCH_GAP = 72;

type Axis = 'v' | 'h' | null;
/** How a point snaps against the other end: onto vertical or level when near
 *  either, or free (Shift held). */
type Snap = 'free' | 'near';

export interface MeasureToolOptions {
    parent: HTMLElement;            // uiRoot, overlays the canvas
    canvas: HTMLCanvasElement;
    app: pc.AppBase;
    cameraEntity: pc.Entity;
    splats: () => Iterable<pc.Entity>;
    /** The authored floors (walk surfaces). Tested on every pick and the
     *  nearer hit wins: a glossy floor is scanned as a mirrored room BELOW it,
     *  so without this the ray goes through the floor. Origin + unit direction. */
    floorPick?: (ro: pc.Vec3, rd: pc.Vec3) => pc.Vec3 | null;
    /** The collision voxels, when the scene has them and they have loaded:
     *  their first hit along the ray is the surface. The splat pick can see
     *  through plain white walls; the voxels don't. Null = not there. */
    voxelPick?: (ro: pc.Vec3, rd: pc.Vec3) => pc.Vec3 | null;
    /** Told when the tool turns on or off, so navigation can give up taps. */
    onActiveChange?: (on: boolean) => void;
    /** Told when a held finger starts and stops aiming (touch), so the view
     *  does not swing under it. */
    onAimChange?: (on: boolean) => void;
}

interface Measurement {
    a: pc.Vec3;
    b: pc.Vec3;
    lines: SVGLineElement[];        // shadow, then the coloured line
    dots: HTMLDivElement[];
    label: HTMLDivElement;
    text: HTMLSpanElement;
    del: HTMLButtonElement;
    /** Which of OCC_SAMPLES points along the line the camera can see, or
     *  null for all of them; refreshed every OCC_EVERY_MS (occlusion()). */
    seen: boolean[] | null;
    seenAt: number;
}

/* A measurement behind a wall must not be drawn over that wall (Ilias,
 * 04/11/2026). The overlay is flat HTML/SVG, so the depth test is ours:
 * points along each line are checked against the collision voxels, and the
 * parts with a wall in front are left out. Slack: an end may sit up to
 * SKIN_BEHIND_M behind the voxel face it was picked on, plus a voxel. */
const OCC_SAMPLES = 24;
const OCC_EVERY_MS = 80;
const OCC_SLACK_M = 0.25;

const tmpScreen = new pc.Vec3();
const tmpDir = new pc.Vec3();
const tmpNear = new pc.Vec3();
const tmpFar = new pc.Vec3();
const occP = new pc.Vec3();
const occDir = new pc.Vec3();
const occFrom = new pc.Vec3();

export class MeasureTool {
    readonly label: string;

    private opts: MeasureToolOptions;
    private lang: UiLang;
    private active = false;
    /** A measurement is being placed (the + was pressed, or the ruler just
     *  turned on). Off: taps belong to the walk controls again. */
    private placing = false;
    private button: HTMLButtonElement | null = null;
    private tip: HTMLDivElement | null = null;
    private tipHeld = false;
    /** What the tip points at: the ruler, or the folded dock's Menu button. */
    private tipAnchor: HTMLElement | null = null;
    /** Tapping the anchor (the phone menu's cog) closes the tip. */
    private tipDismiss: (() => void) | null = null;
    private tipTimer = 0;

    private measurements: Measurement[] = [];
    /** First point of the measurement being placed. */
    private pending: { p: pc.Vec3; dot: HTMLDivElement } | null = null;
    private selected: Measurement | null = null;

    private root: HTMLDivElement;
    private svg: SVGSVGElement;
    private hint: HTMLDivElement;
    private hintText: HTMLSpanElement;
    private hintTimer = 0;
    private help: HTMLDivElement | null = null;
    /** Bottom dock: the bin and the big + (a × while placing). */
    private dock: HTMLDivElement;
    private addBtn: HTMLButtonElement;
    private addText: HTMLSpanElement;
    private trashBtn: HTMLButtonElement;
    /** Walkthrough step: 0-3 the four actions, 4 the closing card, -1 off. */
    private coachStep = -1;
    private coachPulse: Element[] = [];

    /** A point being dragged to a new spot. */
    private drag: {
        m: Measurement; end: 0 | 1; id: number; x: number; y: number; x0: number; y0: number; moving: boolean;
    } | null = null;
    /** The hover point is an existing point (the cursor snapped onto it). */
    private hoverIsPoint = false;
    private dragTimer = 0;

    /** Where a click would land right now (mouse hover), or null. */
    private hover: pc.Vec3 | null = null;
    /** The axis the line to the hover point is snapped to. */
    private hoverAxis: Axis = null;
    /** Snap mode from the keys held during the last mouse event. */
    private snap: Snap = 'free';
    private loupe: HTMLCanvasElement;
    private loupeCtx: CanvasRenderingContext2D | null;
    private loupeOn = false;
    private hoverPos: { x: number; y: number } | null = null;
    private hoverDot: HTMLDivElement;
    private rubber: SVGLineElement[];
    private rubberLabel: HTMLDivElement;
    private pickTimer = 0;
    private lastPickAt = 0;

    /** The press being tracked as a possible tap. */
    private press: { id: number; x: number; y: number; t: number } | null = null;
    /** A held finger is aiming with the magnifier (touch). */
    private aim: { id: number } | null = null;
    /** The magnifier sits above the pointer (a finger) rather than below. */
    private loupeAbove = false;
    private aimTimer = 0;
    private pointersDown = 0;
    private listening = false;

    private onUpdate = () => this.reposition();
    private onFrameEnd = () => this.drawLoupe();

    constructor(opts: MeasureToolOptions) {
        this.opts = opts;
        this.lang = resolveLang();
        this.label = TEXT[this.lang].button;

        this.root = document.createElement('div');
        this.root.className = 'ollo-measure';
        // Inline, not in the stylesheet: `.ollo-ui > *` turns pointer events
        // back on for every direct child, and this layer covers the canvas.
        this.root.style.pointerEvents = 'none';
        opts.parent.appendChild(this.root);

        this.svg = document.createElementNS(SVG_NS, 'svg');
        this.svg.classList.add('ollo-measure-svg');
        this.root.appendChild(this.svg);

        this.hint = document.createElement('div');
        this.hint.className = 'ollo-measure-hint';
        this.hintText = document.createElement('span');
        const helpBtn = document.createElement('button');
        helpBtn.type = 'button';
        helpBtn.className = 'ollo-measure-hint-help';
        helpBtn.textContent = '?';
        helpBtn.title = TEXT[this.lang].help;
        helpBtn.setAttribute('aria-label', TEXT[this.lang].help);
        helpBtn.style.pointerEvents = 'auto';
        helpBtn.addEventListener('pointerdown', e => e.stopPropagation());
        helpBtn.addEventListener('click', (e) => { e.stopPropagation(); this.showHelp(); });
        this.hint.append(this.hintText, helpBtn);
        this.root.appendChild(this.hint);

        this.rubber = ['ollo-measure-line-shadow preview', 'ollo-measure-line preview'].map((cls) => {
            const l = document.createElementNS(SVG_NS, 'line');
            l.setAttribute('class', cls);
            l.style.display = 'none';
            this.svg.appendChild(l);
            return l;
        });
        this.dock = document.createElement('div');
        this.dock.className = 'ollo-measure-dock';
        const dockBtn = (cls: string, label: string, svg: string, fn: () => void) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = cls;
            b.title = label;
            b.setAttribute('aria-label', label);
            b.innerHTML = svg;
            b.style.pointerEvents = 'auto';
            b.addEventListener('pointerdown', e => e.stopPropagation());
            b.addEventListener('click', (e) => { e.stopPropagation(); fn(); });
            this.dock.appendChild(b);
            return b;
        };
        this.trashBtn = dockBtn('ollo-measure-trash', TEXT[this.lang].del, ICON_BIN, () => {
            if (this.selected) this.remove(this.selected);
        });
        this.trashBtn.disabled = true;
        this.addBtn = dockBtn('ollo-measure-add', TEXT[this.lang].add, ICON_PLUS, () => {
            this.select(null);
            this.setPlacing(!this.placing);
            if (this.placing && this.coachStep === 5) this.coachTo(TOUR_DONE);
        });
        this.addText = document.createElement('span');
        this.addText.textContent = TEXT[this.lang].add;
        this.addBtn.appendChild(this.addText);
        this.root.appendChild(this.dock);

        this.loupe = document.createElement('canvas');
        this.loupe.className = 'ollo-measure-loupe';
        this.loupe.style.display = 'none';
        this.loupeCtx = this.loupe.getContext('2d');
        this.root.appendChild(this.loupe);
        this.hoverDot = document.createElement('div');
        this.hoverDot.className = 'ollo-measure-cursor';
        this.hoverDot.style.display = 'none';
        this.root.appendChild(this.hoverDot);
        this.rubberLabel = document.createElement('div');
        this.rubberLabel.className = 'ollo-measure-label preview';
        this.rubberLabel.style.display = 'none';
        this.root.appendChild(this.rubberLabel);

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onPointerCancel = this.onPointerCancel.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
    }

    /** The toolbar button that toggles the tool; gets the .active state. */
    bindButton(b: HTMLButtonElement): void {
        this.button = b;
        b.setAttribute('aria-pressed', String(this.active));
        if (!tipSeen() && !this.tipHeld) this.tipTimer = window.setTimeout(() => this.showTip(), TIP_DELAY_MS);
    }

    /** The onboarding guide is up: no tip competing with it. */
    holdTip(): void {
        this.tipHeld = true;
        this.hideTip();
    }

    /** The guide has finished or been skipped: the tip follows it, every
     *  time (Ilias, 04/11/2026), even for someone who has measured before,
     *  since they asked to be shown around. */
    releaseTip(): void {
        this.tipHeld = false;
        window.clearTimeout(this.tipTimer);
        this.tipTimer = window.setTimeout(() => this.showTip(), 700);
    }

    /** A small bubble under the ruler saying measuring exists. */
    private showTip(): void {
        const b = this.button;
        if (!b || this.active || this.tipHeld || this.tip || !b.isConnected) return;
        const tip = document.createElement('div');
        tip.className = 'ollo-measure-tip';
        const icon = document.createElement('span');
        icon.className = 'ollo-measure-tip-icon';
        icon.innerHTML = ICON_RULER;
        const text = document.createElement('span');
        text.textContent = TEXT[this.lang].tip;
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'ollo-measure-tip-close';
        close.setAttribute('aria-label', 'Close');
        close.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        tip.append(icon, text, close);
        tip.addEventListener('pointerdown', e => e.stopPropagation());
        tip.addEventListener('click', () => { this.hideTip(); this.setActive(true); });
        close.addEventListener('click', (e) => { e.stopPropagation(); this.hideTip(); });
        // Folded away in the phone menu: point at its cog instead.
        const anchor: HTMLElement = b.getBoundingClientRect().height > 4
            ? b
            : b.closest('.ollo-toolbar')?.querySelector<HTMLElement>('.ollo-dock-menu') ?? b;
        anchor.classList.add('ollo-measure-glow');
        this.tipAnchor = anchor;
        this.tipDismiss = () => this.hideTip();
        anchor.addEventListener('click', this.tipDismiss);
        this.opts.parent.appendChild(tip);
        // Next to the button, right edges aligned, the arrow pointing at it:
        // under it in a top row, above it in the bottom dock.
        const pr = this.opts.parent.getBoundingClientRect();
        const br = anchor.getBoundingClientRect();
        if (br.top > pr.top + pr.height / 2) {
            tip.classList.add('above');
            tip.style.bottom = `${pr.bottom - br.top + 12}px`;
        } else {
            tip.style.top = `${br.bottom - pr.top + 10}px`;
        }
        tip.style.right = `${pr.right - br.right}px`;
        tip.style.setProperty('--arrow-right', `${br.width / 2 - 6}px`);
        // Right edges aligned can push the bubble off the left edge (on a
        // phone the ruler is near the pill's left end): slide it back on
        // screen, the arrow moving the other way so it still points at it.
        // (measured mid-animation at 96%, hence the extra margin)
        const overflow = pr.left + 20 - tip.getBoundingClientRect().left;
        if (overflow > 0) {
            tip.style.right = `${pr.right - br.right - overflow}px`;
            tip.style.setProperty('--arrow-right', `${br.width / 2 - 6 + overflow}px`);
        }
        this.tip = tip;
        requestAnimationFrame(() => tip.classList.add('visible'));
        this.tipTimer = window.setTimeout(() => this.hideTip(), TIP_SHOW_MS);
    }

    private hideTip(): void {
        window.clearTimeout(this.tipTimer);
        const tip = this.tip;
        this.tip = null;
        if (!tip) return;
        tip.classList.remove('visible');
        this.tipAnchor?.classList.remove('ollo-measure-glow');
        if (this.tipDismiss) this.tipAnchor?.removeEventListener('click', this.tipDismiss);
        this.tipDismiss = null;
        this.tipAnchor = null;
        window.setTimeout(() => tip.remove(), 300);
    }

    toggle(): void {
        this.setActive(!this.active);
    }

    setActive(on: boolean): void {
        if (on === this.active) return;
        this.active = on;
        const c = this.opts.canvas;
        if (on) {
            c.addEventListener('pointerdown', this.onPointerDown);
            // window, not canvas: the controllers capture the pointer, and an
            // up outside the canvas must still end the press.
            window.addEventListener('pointerup', this.onPointerUp);
            window.addEventListener('pointercancel', this.onPointerCancel);
            c.addEventListener('pointermove', this.onPointerMove);
            c.addEventListener('pointerleave', this.onPointerLeave);
            this.root.classList.add('active');
        } else {
            c.removeEventListener('pointerdown', this.onPointerDown);
            window.removeEventListener('pointerup', this.onPointerUp);
            window.removeEventListener('pointercancel', this.onPointerCancel);
            c.removeEventListener('pointermove', this.onPointerMove);
            c.removeEventListener('pointerleave', this.onPointerLeave);
            this.root.classList.remove('active');
            this.setPlacing(false);
            this.select(null);
            this.press = null;
            this.pointersDown = 0;
        }
        if (on) {
            this.hideTip();
            markTipSeen();
        } else {
            this.hideHelp();
        }
        this.button?.classList.toggle('active', on);
        this.button?.setAttribute('aria-pressed', String(on));
        // straight into the first measurement: the ruler means "measure now"
        if (on) this.setPlacing(true);
        this.showHint(on ? this.instruction() : null);
        if (on && !seen(HELP_SEEN_KEY)) this.showHelp();
        this.syncListening();
    }

    /** Start or stop placing a measurement. Stopping drops a half-placed one. */
    private setPlacing(on: boolean): void {
        if (on === this.placing) return;
        this.placing = on;
        if (!on) {
            this.clearHover();
            this.dropPending();
        }
        this.opts.canvas.classList.toggle('ollo-measuring', on);
        this.addBtn.classList.toggle('cancel', on);
        const label = on ? TEXT[this.lang].cancel : TEXT[this.lang].add;
        this.addText.textContent = label;
        this.addBtn.title = label;
        this.addBtn.setAttribute('aria-label', label);
        this.opts.onActiveChange?.(on);
        if (this.active) this.showHint(this.instruction());
    }

    private coarse(): boolean {
        return !!window.matchMedia?.('(pointer: coarse)').matches;
    }

    /** Start the first-measurement walkthrough (first switch-on, or the "?"). */
    private showHelp(): void {
        if (!this.active) this.setActive(true);
        this.coachStep = 0;
        this.renderCoach();
    }

    /** The walkthrough card for the current step, under the toolbar. Steps
     *  0-1 wait for the visitor to place the points; 2-3 also offer Next. */
    private renderCoach(): void {
        const t = this.coarse() ? TEXT[this.lang].touch : TEXT[this.lang].mouse;
        const step = this.coachStep;
        let card = this.help;
        if (!card) {
            card = document.createElement('div');
            card.className = 'ollo-measure-help';
            card.style.pointerEvents = 'auto';
            card.addEventListener('pointerdown', e => e.stopPropagation());
            this.root.appendChild(card);
            this.help = card;
            requestAnimationFrame(() => card!.classList.add('visible'));
        }
        card.innerHTML = '';
        this.hint.style.visibility = 'hidden';

        const done = step >= t.steps.length;
        const head = document.createElement('div');
        head.className = 'ollo-measure-help-title';
        head.innerHTML = ICON_RULER;
        head.append(done ? t.doneTitle : t.title);
        const body = document.createElement('div');
        body.className = 'ollo-measure-help-body';
        body.textContent = done ? t.doneText : t.steps[step];
        const foot = document.createElement('div');
        foot.className = 'ollo-measure-help-foot';
        const dots = document.createElement('div');
        dots.className = 'ollo-measure-help-dots';
        if (!done) {
            dots.title = t.of.replace('{n}', String(step + 1)).replace('{m}', String(t.steps.length));
            t.steps.forEach((_, i) => {
                const d = document.createElement('span');
                if (i < step) d.className = 'done';
                else if (i === step) d.className = 'current';
                dots.appendChild(d);
            });
        }
        const buttons = document.createElement('div');
        buttons.className = 'ollo-measure-help-buttons';
        const btn = (label: string, cls: string, fn: () => void) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = cls;
            b.textContent = label;
            b.addEventListener('click', (e) => { e.stopPropagation(); fn(); });
            buttons.appendChild(b);
        };
        if (done) {
            btn(t.ok, 'ollo-measure-help-ok', () => this.hideHelp());
        } else {
            btn(t.skip, 'ollo-measure-help-skip', () => this.hideHelp());
            // dragging and deleting can be skipped; skipping the select skips the delete too
            if (step >= 2 && step <= 4) btn(t.next, 'ollo-measure-help-ok', () => this.coachTo(step === 3 ? 5 : step + 1));
        }
        foot.append(dots, buttons);
        card.append(head, body, foot);
        this.updateCoachPulse();
    }

    /** Move the walkthrough on to `step` (never backwards). */
    private coachTo(step: number): void {
        if (this.coachStep < 0 || step <= this.coachStep) return;
        this.coachStep = step;
        this.renderCoach();
    }

    /** The element the current step is about pulses: the newest line's
     *  points while dragging is taught, its label while selecting is, the bin
     *  and the label's × while deleting is, the + after that. */
    private updateCoachPulse(): void {
        this.coachPulse.forEach(el => el.classList.remove('ollo-coach-pulse'));
        this.coachPulse = [];
        const last = this.measurements[this.measurements.length - 1];
        const step = this.coachStep;
        if (step === 2 && last) this.coachPulse = [...last.dots];
        else if (step === 3 && last) this.coachPulse = [last.label];
        else if (step === 4) this.coachPulse = [this.trashBtn, ...(this.selected ? [this.selected.del] : [])];
        else if (step === 5) this.coachPulse = [this.addBtn];
        this.coachPulse.forEach(el => el.classList.add('ollo-coach-pulse'));
    }

    private hideHelp(): void {
        const card = this.help;
        this.coachStep = -1;
        this.updateCoachPulse();
        if (!card) return;
        this.help = null;
        markSeen(HELP_SEEN_KEY);
        this.hint.style.visibility = '';
        this.showHint(this.active ? this.instruction() : null);
        card.classList.remove('visible');
        window.setTimeout(() => card.remove(), 250);
    }

    /** The line in the top pill, or null: once the measuring tour has been
     *  done (or skipped) the pill says nothing more (Ilias, 04/11/2026);
     *  only the short "nothing there" flash still uses it. */
    private instruction(): string | null {
        if (seen(HELP_SEEN_KEY)) return null;
        if (!this.placing) return TEXT[this.lang].idle;
        if (this.pending) return TEXT[this.lang].second;
        return this.coarse() ? TEXT[this.lang].tap : TEXT[this.lang].click;
    }

    /** Follow the camera only while something is on screen to follow. */
    private syncListening(): void {
        const need = this.measurements.length > 0 || this.pending !== null || this.hover !== null;
        if (need === this.listening) return;
        this.listening = need;
        if (need) this.opts.app.on('update', this.onUpdate);
        else this.opts.app.off('update', this.onUpdate);
    }

    private onPointerDown(e: PointerEvent): void {
        this.pointersDown++;
        // A second finger means pinch/dolly, never a tap.
        if (this.pointersDown > 1 || !e.isPrimary || e.button > 0) {
            this.press = null;
            window.clearTimeout(this.aimTimer);
            if (this.aim) this.endAim();
            return;
        }
        this.press = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp };
        // touch: a finger held still while placing brings up the magnifier
        if (e.pointerType !== 'mouse' && this.placing) {
            window.clearTimeout(this.aimTimer);
            const id = e.pointerId;
            this.aimTimer = window.setTimeout(() => {
                const p = this.press;
                if (p && p.id === id && this.pointersDown === 1 && this.placing) this.startAim(id, p.x, p.y);
            }, AIM_HOLD_MS);
        }
    }

    private startAim(id: number, x: number, y: number): void {
        this.aim = { id };
        this.opts.onAimChange?.(true);
        this.hoverPos = { x, y };
        this.snap = 'near';
        this.showLoupe(true);
        this.hoverPick();
    }

    private endAim(): void {
        this.aim = null;
        this.opts.onAimChange?.(false);
        this.clearHover();
    }

    /** Pick under hoverPos soon, at most every HOVER_PICK_MS. */
    private schedulePick(): void {
        if (this.pickTimer) return;
        const wait = Math.max(0, HOVER_PICK_MS - (performance.now() - this.lastPickAt));
        this.pickTimer = window.setTimeout(() => {
            this.pickTimer = 0;
            this.lastPickAt = performance.now();
            this.hoverPick();
        }, wait);
    }

    private onPointerMove(e: PointerEvent): void {
        // touch: the aiming finger moves the magnifier and the point under it
        if (this.aim && e.pointerId === this.aim.id) {
            this.hoverPos = { x: e.clientX, y: e.clientY };
            this.showLoupe(true);
            this.schedulePick();
            return;
        }
        if (e.pointerType !== 'mouse') {
            // moved before the hold time: a swipe to look, not an aim
            const p = this.press;
            if (p && p.id === e.pointerId && Math.hypot(e.clientX - p.x, e.clientY - p.y) > TAP_MAX_PX) {
                window.clearTimeout(this.aimTimer);
            }
            return;
        }
        if (!this.placing) return;
        // A held button is a look/orbit drag: the view swings, the ring would lag.
        if (e.buttons !== 0) {
            this.clearHover();
            return;
        }
        this.hoverPos = { x: e.clientX, y: e.clientY };
        this.snap = snapFor(e);
        this.showLoupe(true);
        this.schedulePick();
    }

    private onPointerLeave(): void {
        this.clearHover();
    }

    private hoverPick(): void {
        if (!this.placing || !this.hoverPos) return;
        const { x, y } = this.hoverPos;
        const point = this.pointNear(x, y);
        const hit = point ?? this.pick(x, y);
        this.hoverIsPoint = !!point;
        this.hover = hit;
        this.hoverAxis = null;
        this.hoverDot.classList.toggle('magnet', !!point);
        if (hit && !point && this.pending) {
            const s = snapToAxis(this.pending.p, hit, this.snap);
            this.hover = s.p;
            this.hoverAxis = s.axis;
        }
        this.opts.canvas.classList.toggle('ollo-measure-miss', !this.hover);
        if (this.hover && this.pending) {
            this.rubberLabel.textContent = this.formatDistance(this.pending.p.distance(this.hover));
        }
        for (const el of [...this.rubber, this.rubberLabel]) {
            el.classList.toggle('axis-v', this.hoverAxis === 'v');
            el.classList.toggle('axis-h', this.hoverAxis === 'h');
        }
        this.syncListening();
        this.reposition();
    }

    private clearHover(): void {
        window.clearTimeout(this.pickTimer);
        this.pickTimer = 0;
        this.hover = null;
        this.hoverAxis = null;
        this.hoverIsPoint = false;
        this.hoverDot.classList.remove('magnet');
        this.hoverPos = null;
        this.showLoupe(false);
        this.opts.canvas.classList.remove('ollo-measure-miss');
        this.hoverDot.style.display = 'none';
        this.rubber.forEach((l) => { l.style.display = 'none'; });
        this.rubberLabel.style.display = 'none';
        this.syncListening();
    }

    private onPointerCancel(): void {
        this.pointersDown = Math.max(0, this.pointersDown - 1);
        this.press = null;
        window.clearTimeout(this.aimTimer);
        if (this.aim) this.endAim();
    }

    private onPointerUp(e: PointerEvent): void {
        this.pointersDown = Math.max(0, this.pointersDown - 1);
        window.clearTimeout(this.aimTimer);
        // touch: lifting the aiming finger places the point under it
        if (this.aim && e.pointerId === this.aim.id) {
            const pos = this.hoverPos;
            this.press = null;
            this.endAim();
            if (pos && this.placing) this.placeAt(pos.x, pos.y, 'near');
            return;
        }
        const p = this.press;
        if (!p || p.id !== e.pointerId) return;
        this.press = null;
        const moved = Math.hypot(e.clientX - p.x, e.clientY - p.y);
        if (moved > TAP_MAX_PX || e.timeStamp - p.t > TAP_MAX_MS) return;
        // With a measurement selected, a tap on the scene only lets go of it.
        if (this.selected) {
            this.select(null);
            return;
        }
        // between measurements a tap is the walk controls' (click-to-move)
        if (!this.placing) return;
        this.placeAt(e.clientX, e.clientY, snapFor(e));
    }

    /** Shift pressed or let go with the cursor still: re-snap the preview. */
    private onModifier(e: KeyboardEvent): void {
        if (e.key !== 'Shift') return;
        const snap = snapFor(e);
        if (snap === this.snap) return;
        this.snap = snap;
        if (this.drag?.moving) this.dragPick();
        else if (this.hoverPos && !this.hoverIsPoint) this.hoverPick();
    }

    private onKeyUp(e: KeyboardEvent): void {
        this.onModifier(e);
    }

    private onKeyDown(e: KeyboardEvent): void {
        const t = e.target as HTMLElement | null;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        this.onModifier(e);
        if ((e.key === 'Delete' || e.key === 'Backspace') && this.selected) {
            e.preventDefault();
            this.remove(this.selected);
        } else if (e.key === 'Escape') {
            if (this.help) this.hideHelp();
            else if (this.selected) this.select(null);
            else if (this.placing && this.measurements.length > 0) this.setPlacing(false);
            else if (this.active) this.setActive(false);
        }
    }

    private placeAt(clientX: number, clientY: number, snap: Snap): void {
        const point = this.pointNear(clientX, clientY);
        if (point) {
            this.usePoint(point);
            return;
        }
        let hit = this.pick(clientX, clientY);
        if (!hit) {
            this.flashHint(TEXT[this.lang].miss);
            return;
        }
        if (this.pending) hit = snapToAxis(this.pending.p, hit, snap).p;
        this.usePoint(hit);
    }

    /** Start or finish a line at `p`. An existing point is passed as the SAME
     *  vector, so the lines that share it stay joined when it moves. */
    private usePoint(p: pc.Vec3): void {
        // a click on a point between measurements starts the next one there
        if (!this.placing) this.setPlacing(true);
        if (!this.pending) {
            this.pending = { p, dot: this.makeDot() };
            this.coachTo(1);
        } else if (this.pending.p !== p) {
            this.add(this.pending.p, p, this.pending.dot);
            this.pending = null;
            this.setPlacing(false);
            this.coachTo(2);
        }
        this.showHint(this.instruction());
        this.syncListening();
        this.reposition();
    }

    /** The existing point drawn within MAGNET_PX of a client position. */
    private pointNear(clientX: number, clientY: number): pc.Vec3 | null {
        const cam = this.opts.cameraEntity.camera;
        if (!cam || this.measurements.length === 0) return null;
        const rect = this.opts.canvas.getBoundingClientRect();
        const sx = clientX - rect.left, sy = clientY - rect.top;
        const camPos = this.opts.cameraEntity.getPosition();
        const fwd = this.opts.cameraEntity.forward;
        let best: pc.Vec3 | null = null;
        let bestD = MAGNET_PX;
        for (const m of this.measurements) {
            for (const p of [m.a, m.b]) {
                const s = this.project(p, cam, camPos, fwd);
                if (!s) continue;
                const d = Math.hypot(s[0] - sx, s[1] - sy);
                if (d < bestD) { bestD = d; best = p; }
            }
        }
        return best;
    }

    /** Surface point under a client position: the collision voxels when the
     *  scene has them, else the nearer of the splats and the authored floors. */
    private pick(clientX: number, clientY: number): pc.Vec3 | null {
        const rect = this.opts.canvas.getBoundingClientRect();
        const sx = clientX - rect.left, sy = clientY - rect.top;
        const cam = this.opts.cameraEntity.camera;
        if (cam && (this.opts.voxelPick || this.opts.floorPick)) {
            cam.screenToWorld(sx, sy, cam.nearClip, tmpNear);
            cam.screenToWorld(sx, sy, cam.farClip, tmpFar);
            tmpDir.sub2(tmpFar, tmpNear).normalize();
            const voxel = this.opts.voxelPick?.(tmpNear.clone(), tmpDir.clone());
            if (voxel) {
                const t = voxel.distance(tmpNear);
                const skin = pickSplat(this.opts.cameraEntity, this.opts.splats(), sx, sy, rect.width, rect.height,
                    [t - SKIN_FRONT_M, t + SKIN_BEHIND_M]);
                return skin ?? voxel;
            }
        }
        const hit = pickSplat(this.opts.cameraEntity, this.opts.splats(), sx, sy, rect.width, rect.height);
        if (!this.opts.floorPick || !cam) return hit;
        const floor = this.opts.floorPick(tmpNear.clone(), tmpDir.clone());
        if (!floor) return hit;
        if (!hit) return floor;
        const camPos = this.opts.cameraEntity.getPosition();
        return floor.distance(camPos) < hit.distance(camPos) ? floor : hit;
    }

    /** The magnifier follows the mouse while hovering; it is copied from the
     *  canvas on 'frameend' (same task as the render, the only time the
     *  WebGL/WebGPU canvas still holds the frame without preserveDrawingBuffer). */
    private showLoupe(on: boolean): void {
        if (on !== this.loupeOn) {
            this.loupeOn = on;
            if (on) this.opts.app.on('frameend', this.onFrameEnd);
            else this.opts.app.off('frameend', this.onFrameEnd);
            this.loupe.style.display = on ? 'block' : 'none';
        }
        if (on) this.opts.app.renderNextFrame = true;
    }

    private drawLoupe(): void {
        const pos = this.hoverPos;
        const ctx = this.loupeCtx;
        if (!pos || !ctx) return;
        const c = this.opts.canvas;
        const rect = c.getBoundingClientRect();
        const dpr = c.width / rect.width;
        const size = Math.round(LOUPE_PX * window.devicePixelRatio);
        if (this.loupe.width !== size) { this.loupe.width = size; this.loupe.height = size; }
        // source square around the cursor, in canvas pixels
        const src = (LOUPE_PX / LOUPE_ZOOM) * dpr;
        const sx = (pos.x - rect.left) * dpr - src / 2;
        const sy = (pos.y - rect.top) * dpr - src / 2;
        ctx.imageSmoothingEnabled = false;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(c, sx, sy, src, src, 0, 0, size, size);
        // crosshair on the exact spot, dark under light so it shows on any wall
        const m = size / 2, arm = size * 0.16, gap = size * 0.05;
        for (const [style, w] of [['rgba(0,0,0,0.55)', 3], ['#fff', 1.25]] as const) {
            ctx.strokeStyle = style;
            ctx.lineWidth = w * window.devicePixelRatio;
            ctx.beginPath();
            ctx.moveTo(m - arm, m); ctx.lineTo(m - gap, m);
            ctx.moveTo(m + gap, m); ctx.lineTo(m + arm, m);
            ctx.moveTo(m, m - arm); ctx.lineTo(m, m - gap);
            ctx.moveTo(m, m + gap); ctx.lineTo(m, m + arm);
            ctx.stroke();
        }
        // below the cursor, or above it near the bottom edge; above a finger,
        // which hides what is under it, or below it near the top edge
        const left = pos.x - rect.left;
        let top: number;
        if (this.aim || this.loupeAbove) {
            top = pos.y - rect.top - LOUPE_TOUCH_GAP - LOUPE_PX / 2;
            if (top - LOUPE_PX / 2 < 0) top = pos.y - rect.top + LOUPE_TOUCH_GAP + LOUPE_PX / 2;
        } else {
            top = pos.y - rect.top + LOUPE_GAP + LOUPE_PX / 2;
            if (top + LOUPE_PX / 2 > rect.height) top = pos.y - rect.top - LOUPE_GAP - LOUPE_PX / 2;
        }
        this.loupe.style.left = `${left}px`;
        this.loupe.style.top = `${top}px`;
    }

    private makeDot(): HTMLDivElement {
        const d = document.createElement('div');
        d.className = 'ollo-measure-dot';
        this.root.appendChild(d);
        return d;
    }

    private add(a: pc.Vec3, b: pc.Vec3, firstDot: HTMLDivElement): void {
        const lines = ['ollo-measure-line-shadow', 'ollo-measure-line'].map((cls) => {
            const l = document.createElementNS(SVG_NS, 'line');
            l.setAttribute('class', cls);
            this.svg.appendChild(l);
            return l;
        });

        const label = document.createElement('div');
        label.className = 'ollo-measure-label';
        // Labels are the handle for selecting, so they take pointer events, and
        // keep the press from reaching the camera controls underneath.
        label.style.pointerEvents = 'auto';
        const text = document.createElement('span');
        text.textContent = this.formatDistance(a.distance(b));
        // the × shows on the selected label: one tap to pick, one to delete
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'ollo-measure-del';
        del.setAttribute('aria-label', TEXT[this.lang].del);
        del.title = TEXT[this.lang].del;
        del.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        label.append(text, del);
        this.root.appendChild(label);

        const m: Measurement = { a, b, lines, dots: [firstDot, this.makeDot()], label, text, del, seen: null, seenAt: 0 };
        this.makeDraggable(m, 0);
        this.makeDraggable(m, 1);
        label.addEventListener('pointerdown', e => e.stopPropagation());
        label.addEventListener('click', (e) => {
            e.stopPropagation();
            this.select(this.selected === m ? null : m);
        });
        del.addEventListener('click', (e) => {
            e.stopPropagation();
            this.remove(m);
        });
        this.measurements.push(m);
    }

    /** A placed point, while the ruler is on: a click uses it for a line (the
     *  lines stay joined there), a press-and-drag moves it onto whatever
     *  surface is under the pointer. */
    private makeDraggable(m: Measurement, end: 0 | 1): void {
        const dot = m.dots[end];
        dot.classList.add('draggable');
        // The dot's grab area takes the mouse before the canvas sees it, so
        // the "this point will be reused" ring is shown from here.
        dot.addEventListener('pointerenter', (e) => {
            if (!this.placing || this.drag || e.pointerType !== 'mouse') return;
            const p = end === 0 ? m.a : m.b;
            window.clearTimeout(this.pickTimer);
            this.pickTimer = 0;
            this.hover = p;
            this.hoverAxis = null;
            this.hoverIsPoint = true;
            this.hoverDot.classList.add('magnet');
            for (const el of [...this.rubber, this.rubberLabel]) el.classList.remove('axis-v', 'axis-h');
            if (this.pending) this.rubberLabel.textContent = this.formatDistance(this.pending.p.distance(p));
            this.syncListening();
            this.reposition();
        });
        dot.addEventListener('pointerleave', () => {
            if (this.hoverIsPoint && !this.drag) this.clearHover();
        });
        dot.addEventListener('pointerdown', (e) => {
            if (!this.active || e.button > 0) return;
            // keep the press away from the camera controls and from placing
            e.stopPropagation();
            e.preventDefault();
            dot.setPointerCapture(e.pointerId);
            this.drag = { m, end, id: e.pointerId, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, moving: false };
        });
        dot.addEventListener('pointermove', (e) => {
            const d = this.drag;
            if (!d || d.id !== e.pointerId) return;
            d.x = e.clientX; d.y = e.clientY;
            this.snap = snapFor(e);
            if (!d.moving) {
                // a press that has not travelled is still a click
                if (Math.hypot(d.x - d.x0, d.y - d.y0) <= TAP_MAX_PX) return;
                d.moving = true;
                this.coachTo(3);
                dot.classList.add('dragging');
                this.clearHover();
            }
            // the magnifier follows the dragged point, as when placing one
            this.hoverPos = { x: d.x, y: d.y };
            this.loupeAbove = e.pointerType !== 'mouse';
            this.showLoupe(true);
            if (this.dragTimer) return;
            this.dragTimer = window.setTimeout(() => {
                this.dragTimer = 0;
                this.dragPick();
            }, HOVER_PICK_MS);
        });
        const end_ = (e: PointerEvent) => {
            const d = this.drag;
            if (!d || d.id !== e.pointerId) return;
            window.clearTimeout(this.dragTimer);
            this.dragTimer = 0;
            this.drag = null;
            dot.classList.remove('dragging');
            this.showLoupe(false);
            this.hoverPos = null;
            this.loupeAbove = false;
            if (!d.moving) {
                if (e.type === 'pointerup') this.usePoint(end === 0 ? m.a : m.b);
                return;
            }
            d.x = e.clientX; d.y = e.clientY;
            this.drag = d;
            this.dragPick();
            this.drag = null;
        };
        dot.addEventListener('pointerup', end_);
        dot.addEventListener('pointercancel', end_);
    }

    /** Move the dragged point to the surface under the pointer. A miss keeps
     *  the last good spot, so sliding over a gap does not lose the point. */
    private dragPick(): void {
        const d = this.drag;
        if (!d) return;
        let hit = this.pick(d.x, d.y);
        if (!hit) return;
        hit = snapToAxis(d.end === 0 ? d.m.b : d.m.a, hit, this.snap).p;
        // moved in place: every line sharing this point follows
        (d.end === 0 ? d.m.a : d.m.b).copy(hit);
        for (const m of this.measurements) {
            m.text.textContent = this.formatDistance(m.a.distance(m.b));
            m.seenAt = 0;
        }
        this.reposition();
    }

    private select(m: Measurement | null): void {
        if (this.selected) this.setSelectedLook(this.selected, false);
        this.selected = m;
        if (m) this.setSelectedLook(m, true);
        this.trashBtn.disabled = !m;
        if (m && this.coachStep === 3) this.coachTo(4);
    }

    private setSelectedLook(m: Measurement, on: boolean): void {
        m.label.classList.toggle('selected', on);
        m.lines[1].classList.toggle('selected', on);
        m.dots.forEach(d => d.classList.toggle('selected', on));
    }

    private remove(m: Measurement): void {
        if (this.selected === m) this.select(null);
        if (this.coachStep === 4) this.coachTo(5);
        m.lines.forEach(l => l.remove());
        m.dots.forEach(d => d.remove());
        m.label.remove();
        this.measurements = this.measurements.filter(x => x !== m);
        this.syncListening();
    }

    private dropPending(): void {
        this.pending?.dot.remove();
        this.pending = null;
        this.syncListening();
    }

    private showHint(text: string | null): void {
        window.clearTimeout(this.hintTimer);
        this.hintText.textContent = text ?? '';
        this.hint.style.display = text ? 'flex' : 'none';
    }

    private flashHint(text: string): void {
        this.showHint(text);
        this.hintTimer = window.setTimeout(() => {
            this.showHint(this.active ? this.instruction() : null);
        }, 1800);
    }

    private formatDistance(m: number): string {
        const locale = this.lang === 'en' ? 'en-GB' : this.lang;
        // 0.996 m rounds to 100 cm: say 1.00 m instead
        if (Math.round(m * 100) < 100) return `${Math.round(m * 100)} cm`;
        return `${m.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`;
    }

    /** Screen position of a world point, or null when behind the camera. */
    private project(p: pc.Vec3, cam: pc.CameraComponent, camPos: pc.Vec3, fwd: pc.Vec3): [number, number] | null {
        tmpDir.sub2(p, camPos);
        if (tmpDir.dot(fwd) <= 0) return null;
        cam.worldToScreen(p, tmpScreen);
        return [tmpScreen.x, tmpScreen.y];
    }

    private static place(el: HTMLElement, s: [number, number] | null): void {
        if (!s) { el.style.visibility = 'hidden'; return; }
        el.style.visibility = 'visible';
        el.style.left = `${s[0]}px`;
        el.style.top = `${s[1]}px`;
    }

    /** Project everything to screen. Anything behind the camera is hidden. */
    private reposition(): void {
        const cam = this.opts.cameraEntity.camera;
        if (!cam) return;
        const camPos = this.opts.cameraEntity.getPosition();
        const fwd = this.opts.cameraEntity.forward;

        const pend = this.pending ? this.project(this.pending.p, cam, camPos, fwd) : null;
        if (this.pending) MeasureTool.place(this.pending.dot, pend);

        const hov = this.hover ? this.project(this.hover, cam, camPos, fwd) : null;
        this.hoverDot.style.display = hov ? 'block' : 'none';
        if (hov) MeasureTool.place(this.hoverDot, hov);
        const band = pend && hov ? [pend, hov] : null;
        this.rubber.forEach((l) => {
            l.style.display = band ? 'block' : 'none';
            if (!band) return;
            l.setAttribute('x1', String(band[0][0]));
            l.setAttribute('y1', String(band[0][1]));
            l.setAttribute('x2', String(band[1][0]));
            l.setAttribute('y2', String(band[1][1]));
        });
        this.rubberLabel.style.display = band ? 'flex' : 'none';
        if (band) MeasureTool.place(this.rubberLabel, [(band[0][0] + band[1][0]) / 2, (band[0][1] + band[1][1]) / 2]);

        for (const m of this.measurements) {
            const a = this.project(m.a, cam, camPos, fwd);
            const b = this.project(m.b, cam, camPos, fwd);
            const seen = this.occlusion(m, camPos);
            MeasureTool.place(m.dots[0], seen && !seen[0] ? null : a);
            MeasureTool.place(m.dots[1], seen && !seen[OCC_SAMPLES - 1] ? null : b);
            const both = a && b;
            // The visible stretches of the line, as a dash pattern along its
            // screen length (a 3D segment projects to a 2D segment, so the
            // samples' screen distances from A are where the runs change).
            let dash = '';
            let mid: [number, number] | null = both ? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] : null;
            let hidden = false;
            if (both && seen) {
                const at: number[] = [];
                for (let i = 0; i < OCC_SAMPLES; i++) {
                    occP.lerp(m.a, m.b, i / (OCC_SAMPLES - 1));
                    cam.worldToScreen(occP, tmpScreen);
                    at.push(Math.hypot(tmpScreen.x - a[0], tmpScreen.y - a[1]));
                }
                const total = Math.hypot(b[0] - a[0], b[1] - a[1]);
                // A dash pattern starts with a dash: a hidden start is a 0 dash.
                const lens: number[] = seen[0] ? [] : [0];
                let last = 0, start = 0, best = -1, bestEnd = -1;
                for (let i = 1; i <= OCC_SAMPLES; i++) {
                    if (i < OCC_SAMPLES && seen[i] === seen[i - 1]) continue;
                    // a run of equal samples ends: its edge is halfway to the next
                    const edge = i === OCC_SAMPLES ? total : (at[i - 1] + at[i]) / 2;
                    lens.push(edge - last);
                    last = edge;
                    if (seen[i - 1] && (best < 0 || i - 1 - start > bestEnd - best)) { best = start; bestEnd = i - 1; }
                    start = i;
                }
                if (best < 0) {
                    hidden = true;
                    mid = null;
                } else {
                    dash = lens.map(n => n.toFixed(1)).join(' ');
                    // the number sits on the middle of the longest visible run
                    occP.lerp(m.a, m.b, (best + bestEnd) / 2 / (OCC_SAMPLES - 1));
                    mid = this.project(occP, cam, camPos, fwd);
                }
            }
            for (const l of m.lines) {
                l.style.visibility = both && !hidden ? 'visible' : 'hidden';
                if (!both) continue;
                l.setAttribute('x1', String(a[0]));
                l.setAttribute('y1', String(a[1]));
                l.setAttribute('x2', String(b[0]));
                l.setAttribute('y2', String(b[1]));
                l.style.strokeDasharray = dash;
            }
            MeasureTool.place(m.label, mid);
        }
    }

    /** Which samples along the line the camera sees past the voxels; null =
     *  all (no voxels, or the line is being dragged and should stay put). */
    private occlusion(m: Measurement, camPos: pc.Vec3): boolean[] | null {
        const pick = this.opts.voxelPick;
        if (!pick || this.drag?.m === m) return null;
        const now = performance.now();
        if (now - m.seenAt < OCC_EVERY_MS) return m.seen;
        m.seenAt = now;
        const seen: boolean[] = [];
        for (let i = 0; i < OCC_SAMPLES; i++) {
            occP.lerp(m.a, m.b, i / (OCC_SAMPLES - 1));
            occDir.sub2(occP, camPos);
            const dist = occDir.length();
            if (dist < 1e-3) { seen.push(true); continue; }
            occDir.mulScalar(1 / dist);
            const hit = pick(occFrom.copy(camPos), occDir);
            seen.push(!hit || hit.distance(camPos) > dist - (OCC_SLACK_M + 0.02 * dist));
        }
        m.seen = seen.every(Boolean) ? null : seen;
        return m.seen;
    }

    dispose(): void {
        this.setActive(false);
        this.hideTip();
        window.clearTimeout(this.dragTimer);
        window.clearTimeout(this.pickTimer);
        window.clearTimeout(this.aimTimer);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        this.opts.app.off('update', this.onUpdate);
        window.clearTimeout(this.hintTimer);
        this.root.remove();
    }
}

// localStorage can throw (private mode, blocked storage): then the tip just
// shows on every visit.
function tipSeen(): boolean {
    try { return window.localStorage.getItem(TIP_SEEN_KEY) === '1'; } catch { return false; }
}

function markTipSeen(): void {
    markSeen(TIP_SEEN_KEY);
}

function snapFor(e: PointerEvent | KeyboardEvent): Snap {
    return e.shiftKey ? 'free' : 'near';
}

/** `p` moved onto the vertical or the level through `from` when the line
 *  between them is within AXIS_SNAP_DEG of it; otherwise (or free) unchanged. */
function snapToAxis(from: pc.Vec3, p: pc.Vec3, snap: Snap): { p: pc.Vec3; axis: Axis } {
    const dx = p.x - from.x, dy = p.y - from.y, dz = p.z - from.z;
    const flat = Math.hypot(dx, dz);
    if (snap === 'free' || flat + Math.abs(dy) < 1e-4) return { p, axis: null };
    const fromVertical = Math.atan2(flat, Math.abs(dy)) * 180 / Math.PI;
    if (fromVertical < AXIS_SNAP_DEG) return { p: new pc.Vec3(from.x, p.y, from.z), axis: 'v' };
    if (90 - fromVertical < AXIS_SNAP_DEG) return { p: new pc.Vec3(p.x, from.y, p.z), axis: 'h' };
    return { p, axis: null };
}

function seen(key: string): boolean {
    try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
}

function markSeen(key: string): void {
    try { window.localStorage.setItem(key, '1'); } catch { /* not remembered */ }
}
